import { characters as hanziSlugCharacters } from "../content/chinese/hanzi-slug.js";
import type {
  ChartSyllable,
  ChartTone,
} from "../content/pinyin-chart/pinyin-chart.js";

/** A syllable the search found, and the tone it named where it named one. */
export interface ChartMatch {
  readonly syllable: ChartSyllable;
  readonly tone?: ChartTone;
}

/** The combining marks pinyin writes its tones with, by tone. */
const toneMarks: ReadonlyMap<string, number> = new Map([
  ["̄", 1],
  ["́", 2],
  ["̌", 3],
  ["̀", 4],
]);

/** Whether a string holds a Chinese character. */
const hanzi = /\p{Script=Han}/u;

/**
 * Read what a reader typed as a syllable: its key and the tone, if any.
 *
 * Takes `ma`, `ma3`, `mǎ`, and ü written `ü`, `v` or `u:`. `lu` stays lu. A
 * learner who typed lu may mean lü, and quietly answering with lü would teach
 * them that the two are one syllable, so the chart shows lu and its notes.
 * @param text - What was typed.
 * @returns The key, `lv`, and the tone, or undefined for text that is no
 * syllable spelling at all.
 */
export function readTypedSyllable(
  text: string,
): { readonly key: string; readonly tone?: number } | undefined {
  let tone: number | undefined;
  let letters = "";

  for (const character of text.trim().toLowerCase().normalize("NFD")) {
    const marked = toneMarks.get(character);

    if (marked !== undefined) {
      tone = marked;
    } else if (character === "̈") {
      letters += ":";
    } else {
      letters += character;
    }
  }

  const digit = /([1-5])$/u.exec(letters);

  if (digit !== null) {
    tone = Number(digit[1]);
    letters = letters.slice(0, -1);
  }

  const key = letters.replaceAll("u:", "v");

  if (!/^[a-zê]+$/u.test(key.normalize("NFC"))) {
    return undefined;
  }

  return { key: key.normalize("NFC"), ...(tone !== undefined && { tone }) };
}

/**
 * Find what a reader typed in the chart.
 *
 * A syllable, in any of the spellings {@link readTypedSyllable} reads, or one
 * or more characters. A character is found under every reading the 词典 gives
 * it, so 长 finds cháng and zhǎng.
 * @param text - What was typed.
 * @param syllables - The chart's syllables.
 * @returns The matches, in the chart's order.
 */
export function searchChart(
  text: string,
  syllables: readonly ChartSyllable[],
): readonly ChartMatch[] {
  const characters = hanziSlugCharacters(text.trim()).filter((character) =>
    hanzi.test(character),
  );

  if (characters.length > 0) {
    const wanted = characters[0] ?? "";

    return syllables.flatMap((syllable) =>
      syllable.tones
        .filter((tone) => tone.characters.includes(wanted))
        .map((tone) => ({ syllable, tone })),
    );
  }

  const typed = readTypedSyllable(text);

  if (typed === undefined) {
    return [];
  }

  const syllable = syllables.find(
    (candidate) =>
      candidate.key === typed.key ||
      candidate.pinyin.normalize("NFC") === typed.key,
  );

  if (syllable === undefined) {
    return [];
  }

  const tone = syllable.tones.find((found) => found.tone === typed.tone);

  return [{ syllable, ...(tone !== undefined && { tone }) }];
}
